import { TransactionResponse } from '@ethersproject/abstract-provider';
import { BigNumber } from '@ethersproject/bignumber';
import { Contract } from '@ethersproject/contracts';
import { claims } from '@manifoldxyz/claim-contracts';
import { Constants, Network } from '@manifoldxyz/js-ts-utils';
import { CLAIM_FEE_PER_MERKLE_MINT, CLAIM_FEE_PER_MINT } from '@/common/constants';
import { isWalletConnect } from '@/common/util';
import BaseContract from '@/contracts/baseContract';

const { CLAIM_EXTENSION_721_V1_MAINNET, CLAIM_EXTENSION_1155_V1_MAINNET } = claims;

export enum StorageProtocol {
  INVALID,
  NONE,
  ARWEAVE,
  IPFS,
}

export type Claim = {
  total: number;
  totalMax: number | null;
  walletMax: number | null;
  startDate: Date | null;
  endDate: Date | null;
  storageProtocol: StorageProtocol;
  merkleRoot: string;
  location: string;
  tokenId: BigNumber | null;
  cost: BigNumber;
};

export interface ContractError {
  code: string;
  cancelled: boolean;
  replacement: { hash: string };
}

export const StorageProtocolGateway = {
  [StorageProtocol.ARWEAVE]: 'https://arweave.net/',
  [StorageProtocol.IPFS]: 'https://ipfs.io/',
  [StorageProtocol.NONE]: '',
  [StorageProtocol.INVALID]: '',
} as const;

export enum ClaimType {
  ERC721,
  ERC1155,
}

export default class ClaimExtensionContract extends BaseContract {
  private extensionContractAddress: string;
  private creatorContractAddress: string;
  private claimIndex: number;

  constructor(
    networkId: Network.NetworkId,
    extensionContractAddress: string,
    creatorContractAddress: string,
    claimIndex: number,
  ) {
    super(networkId);
    this.extensionContractAddress = extensionContractAddress;
    this.creatorContractAddress = creatorContractAddress;
    this.claimIndex = claimIndex;
  }

  get ext() {
    const ext = claims.getExtension(this.extensionContractAddress) as claims.ClaimDeployment;
    if (!ext) {
      throw new Error('Claim extension not found on @manifoldxyz/claim-contracts');
    }
    return ext;
  }

  async getClaim(spec: ClaimType): Promise<Claim> {
    const claimArray = await this._callWeb3WithServerFallback(
      this.extensionContractAddress,
      this.ext.abi,
      'getClaim',
      [this.creatorContractAddress, this.claimIndex],
      false,
    );
    return this.processGetClaimResult(claimArray, spec, this.ext);
  }

  async checkMintIndices(mintIndices: number[]): Promise<boolean[]> {
    return this._callWeb3WithServerFallback(
      this.extensionContractAddress,
      this.ext.abi,
      'checkMintIndices',
      [this.creatorContractAddress, this.claimIndex, mintIndices],
      false,
    );
  }

  async getTotalMints(walletAddress: string): Promise<number> {
    return this._callWeb3WithServerFallback(
      this.extensionContractAddress,
      this.ext.abi,
      'getTotalMints',
      [walletAddress, this.creatorContractAddress, this.claimIndex],
      false,
    );
  }

  async getMintFee(): Promise<BigNumber> {
    if (claims.hasTrait(this.ext, claims.Trait.UpdateeMintFee)) {
      return this._callWeb3WithServerFallback(
        this.extensionContractAddress,
        this.ext.abi,
        'MINT_FEE',
        [],
        false,
      );
    }

    return CLAIM_FEE_PER_MINT;
  }

  async getMerkleMintFee(): Promise<BigNumber> {
    if (claims.hasTrait(this.ext, claims.Trait.UpdateeMintFee)) {
      return this._callWeb3WithServerFallback(
        this.extensionContractAddress,
        this.ext.abi,
        'MINT_FEE_MERKLE',
        [],
        false,
      );
    }
    return CLAIM_FEE_PER_MERKLE_MINT;
  }

  /**
   * Used to mint one ERC721 or one edition of an ERC1155 token at a time
   */
  async mint(
    account: string,
    mintIndices: number[],
    merkleProofs: string[] = [],
    mintPrice: {
      value: string; // big number string
      currency: string;
      erc20: string;
      decimals: number;
    },
    mintForAddress: string, // this is either the wallet address of the user or the delegate address
  ): Promise<TransactionResponse | undefined> {
    const abi = this.ext.abi;
    const value = await this.getMintValue(1, mintPrice, mintIndices.length > 0);
    const valueString = value.toString();
    const mintIndex = mintIndices?.[0] || 0;

    try {
      const unchecked = isWalletConnect();

      if (claims.hasTrait(this.ext, claims.Trait.DelegateMint)) {
        const gasEstimate = await this._estimateGasWithServerFallback(
          this.network,
          this.extensionContractAddress,
          abi,
          'mint',
          [
            this.creatorContractAddress,
            this.claimIndex,
            mintIndex,
            merkleProofs,
            mintForAddress.toLowerCase(),
            {
              from: account,
              value: valueString,
            },
          ],
        );

        return await this._getContractInstance(
          this.extensionContractAddress,
          abi,
          true,
          false,
          unchecked,
        ).mint(
          this.creatorContractAddress,
          this.claimIndex,
          mintIndex,
          merkleProofs,
          mintForAddress.toLowerCase(),
          {
            gasLimit: gasEstimate,
            value: valueString,
          },
        );
      } else {
        const gasEstimate = await this._estimateGasWithServerFallback(
          this.network,
          this.extensionContractAddress,
          abi,
          'mint',
          [
            this.creatorContractAddress,
            this.claimIndex,
            mintIndex,
            merkleProofs,
            {
              from: account,
              value: valueString,
            },
          ],
        );

        return await this._getContractInstance(
          this.extensionContractAddress,
          abi,
          true,
          false,
          unchecked,
        ).mint(this.creatorContractAddress, this.claimIndex, mintIndex, merkleProofs, {
          gasLimit: gasEstimate,
          value: valueString,
        });
      }
    } catch (e: unknown) {
      await this.errorHandling(e as ContractError);
    }
  }

  /**
   * Used for generally minting more than one token at a time
   */
  async mintBatch(
    account: string,
    mintIndices: number[],
    merkleProofs: string[][] = [],
    mintPrice: {
      value: string; // big number string
      currency: string;
      erc20: string;
      decimals: number;
    },
    mintForAddress: string,
    numberOfMints: number,
  ): Promise<TransactionResponse | undefined> {
    const value = await this.getMintValue(numberOfMints, mintPrice, mintIndices.length > 0);

    const valueString = value.toString();
    const abi = this.ext.abi;

    try {
      const unchecked = isWalletConnect();

      if (claims.hasTrait(this.ext, claims.Trait.DelegateMint)) {
        const gasEstimate = await this._estimateGasWithServerFallback(
          this.network,
          this.extensionContractAddress,
          abi,
          'mintBatch',
          [
            this.creatorContractAddress,
            this.claimIndex,
            numberOfMints,
            mintIndices,
            merkleProofs,
            mintForAddress,
            {
              from: account,
              value: valueString,
            },
          ],
        );

        return await this._getContractInstance(
          this.extensionContractAddress,
          abi,
          true,
          false,
          unchecked,
        ).mintBatch(
          this.creatorContractAddress,
          this.claimIndex,
          numberOfMints,
          mintIndices,
          merkleProofs,
          mintForAddress,
          {
            gasLimit: gasEstimate,
            value: valueString,
          },
        );
      } else {
        const gasEstimate = await this._estimateGasWithServerFallback(
          this.network,
          this.extensionContractAddress,
          abi,
          'mintBatch',
          [
            this.creatorContractAddress,
            this.claimIndex,
            numberOfMints,
            mintIndices,
            merkleProofs,
            {
              from: account,
              value: valueString,
            },
          ],
        );

        return await this._getContractInstance(
          this.extensionContractAddress,
          abi,
          true,
          false,
          unchecked,
        ).mintBatch(
          this.creatorContractAddress,
          this.claimIndex,
          numberOfMints,
          mintIndices,
          merkleProofs,
          {
            gasLimit: gasEstimate,
            value: valueString,
          },
        );
      }
    } catch (e: unknown) {
      await this.errorHandling(e as ContractError);
    }
  }

  /**
   * Used for
   * - crossmint
   * - gifting
   *
   * Cannot be used with
   * - delegate minting
   * - allowlist minting
   */
  async mintProxy(
    account: string,
    mintIndices: number[],
    merkleProofs: string[][] = [],
    mintPrice: {
      value: string; // big number string
      currency: string;
      erc20: string;
      decimals: number;
    },
    mintForAddress: string,
    numberOfMints: number,
  ): Promise<TransactionResponse | undefined> {
    const value = await this.getMintValue(numberOfMints, mintPrice, mintIndices.length > 0);

    const valueString = value.toString();
    const abi = this.ext.abi;

    try {
      const unchecked = isWalletConnect();

      if (claims.hasTrait(this.ext, claims.Trait.Proxy)) {
        const gasEstimate = await this._estimateGasWithServerFallback(
          this.network,
          this.extensionContractAddress,
          abi,
          'mintProxy',
          [
            this.creatorContractAddress,
            this.claimIndex,
            numberOfMints,
            mintIndices,
            merkleProofs,
            mintForAddress,
            {
              from: account,
              value: valueString,
            },
          ],
        );

        return await this._getContractInstance(
          this.extensionContractAddress,
          abi,
          true,
          false,
          unchecked,
        ).mintProxy(
          this.creatorContractAddress,
          this.claimIndex,
          numberOfMints,
          mintIndices,
          merkleProofs,
          mintForAddress,
          {
            gasLimit: gasEstimate,
            value: valueString,
          },
        );
      } else {
        throw new Error('Claim extension does not have mint proxy function nor trait');
      }
    } catch (e: unknown) {
      await this.errorHandling(e as ContractError);
    }
  }

  async mintSignature(
    numberOfMints: number,
    signature: string,
    message: string,
    nonce: string,
    expiration: number,
    mintPrice: {
      value: string; // big number string
      currency: string;
      erc20: string;
      decimals: number;
    },
    walletAddress: string,
    mintForAddress: string,
  ): Promise<TransactionResponse | undefined> {
    try {
      const value = await this.getMintValue(numberOfMints, mintPrice, false);

      const valueString = value.toString();
      const abi = this.ext.abi;

      if (claims.hasTrait(this.ext, claims.Trait.Signature)) {
        const gasEstimate = await this._estimateGasWithServerFallback(
          this.network,
          this.extensionContractAddress,
          abi,
          'mintSignature',
          [
            this.creatorContractAddress,
            this.claimIndex,
            numberOfMints,
            signature,
            message,
            nonce,
            mintForAddress,
            expiration,
            {
              from: walletAddress,
              value: valueString,
            },
          ],
        );

        const unchecked = isWalletConnect();

        return await this._getContractInstance(
          this.extensionContractAddress,
          abi,
          true,
          false,
          unchecked,
        ).mintSignature(
          this.creatorContractAddress,
          this.claimIndex,
          numberOfMints,
          signature,
          message,
          nonce,
          mintForAddress,
          expiration,
          {
            gasLimit: gasEstimate,
            value: valueString,
          },
        );
      } else {
        throw new Error('Claim extension does not have mint signature function nor trait');
      }
    } catch (e: unknown) {
      await this.errorHandling(e as ContractError);
    }
  }

  private processGetClaimResult(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    claimArray: any[],
    spec: ClaimType,
    ext: claims.ClaimDeployment,
  ): Claim {
    const convertDate = (timestamp: number) =>
      timestamp === 0 ? null : new Date(timestamp * 1000);

    if (spec === ClaimType.ERC721) {
      if (this.extensionContractAddress?.toLowerCase() === CLAIM_EXTENSION_721_V1_MAINNET) {
        return {
          total: claimArray[0],
          totalMax: claimArray[1] === 0 ? null : claimArray[1],
          walletMax: claimArray[2] === 0 ? null : claimArray[2],
          startDate: convertDate(claimArray[3]),
          endDate: convertDate(claimArray[4]),
          storageProtocol: claimArray[5],
          merkleRoot: claimArray[7],
          location: claimArray[8],
          tokenId: null,
          cost: BigNumber.from(0),
        };
      } else if (claims.hasTrait(ext, claims.Trait.ContractVersion)) {
        return {
          total: claimArray[0],
          totalMax: claimArray[1] === 0 ? null : claimArray[1],
          walletMax: claimArray[2] === 0 ? null : claimArray[2],
          startDate: convertDate(claimArray[3]),
          endDate: convertDate(claimArray[4]),
          storageProtocol: claimArray[5],
          merkleRoot: claimArray[8],
          location: claimArray[9],
          tokenId: null,
          cost: BigNumber.from(claimArray[10]),
        };
      } else {
        return {
          total: claimArray[0],
          totalMax: claimArray[1] === 0 ? null : claimArray[1],
          walletMax: claimArray[2] === 0 ? null : claimArray[2],
          startDate: convertDate(claimArray[3]),
          endDate: convertDate(claimArray[4]),
          storageProtocol: claimArray[5],
          merkleRoot: claimArray[7],
          location: claimArray[8],
          tokenId: null,
          cost: BigNumber.from(claimArray[9]),
        };
      }
    } else {
      if (this.extensionContractAddress?.toLowerCase() === CLAIM_EXTENSION_1155_V1_MAINNET) {
        return {
          total: claimArray[0],
          totalMax: claimArray[1] === 0 ? null : claimArray[1],
          walletMax: claimArray[2] === 0 ? null : claimArray[2],
          startDate: convertDate(claimArray[3]),
          endDate: convertDate(claimArray[4]),
          storageProtocol: claimArray[5],
          merkleRoot: claimArray[6],
          location: claimArray[7],
          tokenId: null,
          cost: BigNumber.from(0),
        };
      } else {
        return {
          total: claimArray[0],
          totalMax: claimArray[1] === 0 ? null : claimArray[1],
          walletMax: claimArray[2] === 0 ? null : claimArray[2],
          startDate: convertDate(claimArray[3]),
          endDate: convertDate(claimArray[4]),
          storageProtocol: claimArray[5],
          merkleRoot: claimArray[6],
          location: claimArray[7],
          tokenId: claimArray[8],
          cost: BigNumber.from(claimArray[9]),
        };
      }
    }
  }

  getListeningContract(): Contract {
    return this._getContractInstance(
      this.extensionContractAddress,
      this.ext.abi,
      false,
      true,
      true,
    );
  }

  async errorHandling(error: ContractError): Promise<void> {
    if (error.code === 'TRANSACTION_REPLACED' && !error.cancelled && error.replacement) {
      const provider = window.ManifoldEthereumProvider.provider();
      if (!provider) {
        throw new Error('No web3 provider detected, please refresh the page and try again');
      }
      const tx = await provider.getTransaction(error.replacement.hash);
      if (!tx) {
        throw new Error('Failed Transaction');
      }
      await tx.wait(1);
    } else {
      throw error;
    }
  }

  async getMintValue(
    numberOfMints: number,
    mintPrice: {
      value: string; // big number string
      currency: string;
      erc20: string;
      decimals: number;
    },
    useMerkleFee: boolean,
  ) {
    let value: BigNumber;
    if (mintPrice.erc20 !== Constants.NULL_ADDRESS) {
      // ERC20 is not handled by "value" in the transaction so we set it to 0
      value = BigNumber.from(0);
    } else {
      value = BigNumber.from(mintPrice.value).mul(numberOfMints);
    }

    if (claims.hasTrait(this.ext, claims.Trait.Fee)) {
      const feeToUse = useMerkleFee ? await this.getMerkleMintFee() : await this.getMintFee();
      value = value.add(feeToUse.mul(numberOfMints));
    }

    return value;
  }
}
